// src/pages/HomePage.tsx
import { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, type Variants } from "framer-motion";
import { Card } from "src/features/showcase/components/Card";
import { REGIONES } from "src/features/showcase/data/regions";
import useScrollMask from "src/hooks/useScrollMask";

/** Hash determinístico [0,1) */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return ((h >>> 0) % 100000) / 100000;
}

/** Plan vertical + spring por ítem (lento/suave) */
type MotionPlan = {
  y: number;
  delay: number;
  stiffness: number;
  damping: number;
  mass: number;
  exitLag: number;
};

function buildPlan(names: string[]): MotionPlan[] {
  const baseDelay = 0.18; // ↑ más alto: arranque global más pausado
  const maxJitter = 0.03; // ±30ms: casi simultáneo pero no exacto
  const minMag = 160; // ↑ movimiento más largo
  const maxMag = 260;

  return names.map((name, i) => {
    const r = hash01(name);
    const sign = i % 2 === 0 ? 1 : -1;
    const mag = minMag + Math.floor(r * (maxMag - minMag)); // 160..260
    const jitter = (r - 0.5) * 2 * maxJitter; // -0.03..+0.03

    // Springs más lentos (↓ stiffness) y con amortiguación mayor (↑ damping)
    const stiffness = 220 + Math.floor(r * 200); // 220..420
    const damping = 30 + Math.floor(r * 18); // 30..48
    const mass = 0.8 + r * 0.6; // 0.8..1.4

    const exitLag = 0.04 + r * 0.08; // 0.04..0.12 (salida escalonada lenta)

    return {
      y: sign * mag,
      delay: baseDelay + jitter,
      stiffness,
      damping,
      mass,
      exitLag,
    };
  });
}

const cardVariants: Variants = {
  hidden: (p: MotionPlan) => ({
    opacity: 0,
    y: p.y,
    scale: 0.92,
    filter: "blur(8px)",
  }),
  show: (p: MotionPlan) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      delay: p.delay,
      type: "spring",
      stiffness: p.stiffness,
      damping: p.damping,
      mass: p.mass,
      // Opacidad entra un poco menos “flash”: más perceptible pero aún rápida
      opacity: {
        delay: p.delay,
        duration: 0.18, // antes 0.12
        ease: "linear",
      },
    },
  }),
  exit: (p: MotionPlan) => {
    const total = 0.7; // ↑ salida más lenta
    const tail = 0.2; // ↑ fade final más largo
    const moveDelay = p.exitLag;

    return {
      opacity: 0,
      y: -Math.sign(p.y) * Math.min(Math.abs(p.y) * 0.7, 220),
      scale: 0.9,
      filter: "blur(6px)",
      transition: {
        delay: moveDelay,
        duration: total,
        ease: [0.45, 0, 0.55, 1],
        opacity: {
          delay: moveDelay + (total - tail), // fade al final
          duration: tail,
          ease: "linear",
        },
      },
    };
  },
};

export default function HomePage() {
  // const [iframeRegionId, setIframeRegionId] = useState<number | null>(null);

  const scrollableParentContainer = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useScrollMask(scrollableParentContainer);

  // const regiones = useMemo(
  //   () => REGIONES.slice().sort((a, b) => a.code - b.code),
  //   []
  // );
  const regiones = useMemo(() => REGIONES, []);
  const plans = useMemo(
    () => buildPlan(regiones.map((r) => r.name)),
    [regiones],
  );

  return (
    <motion.div
      className="touch-none fixed inset-0 z-40 w-screen h-screen bg-black/40 text-white font-sans"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { delay: 0.35 } }}
      transition={{ duration: 0.35 }} // entrada/salida del overlay más pausada
    >
      <div
        ref={scrollableParentContainer}
        className="relative w-full h-full overflow-y-auto overflow-x-hidden no-scrollbar max-w-screen-xl mx-auto"
      >
        <div className="relative min-h-full flex flex-col justify-start p-6 sm:p-10 pb-28">
          <motion.div
            className="flex items-center justify-between mb-8 x-bg-red-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            {/* <h2 className="text-xl md:text-2xl font-semibold tracking-tight">
              Regiones de Chile
            </h2> */}
            <img
              src="/assets/logos/rutas-chile-color.svg"
              alt="Subsecretaría de Turismo"
              className="h-14 w-auto"
            />
            <div className="flex space-x-3">
              <button
                onClick={() => navigate("/tourist")}
                className="border-gradient px-3 sm:px-4 py-1.5 text-sm sm:text-base font-medium rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              >
                Territorio
              </button>
              <button
                onClick={() => navigate("/analyst")}
                className="border-gradient px-3 sm:px-4 py-1.5 text-sm sm:text-base font-medium rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              >
                Ver como Analista
              </button>
            </div>
          </motion.div>

          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {regiones.map((region, i) => (
              <motion.li
                key={region.id}
                variants={cardVariants}
                initial="hidden"
                animate="show"
                exit="exit"
                custom={plans[i]}
                layout
                style={{ willChange: "transform, opacity, filter" }}
              >
                <Card
                  id={region.id}
                  title={region.name}
                  imageUrl={region.cover_url}
                  variant="poster"
                  badge={region.roman}
                />
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}
